<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 dark:bg-slate-900"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
    />



    <!-- Main content -->
    <div class="flex flex-col flex-1 min-w-0 w-full">
      <!-- Top bar -->
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="max-w-6xl mx-auto">
          <!-- Page header -->
          <div
            class="flex flex-col justify-between gap-3 mb-4 sm:flex-row sm:items-center"
          >
            <div>
              <h1 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ t("account_settings_title") }}
              </h1>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ t("account_settings_subtitle") }}
              </p>
            </div>
          </div>

          <!-- Card wrapper (tabs + form) -->
          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <!-- Tabs -->
            <header
              class="flex items-center justify-between px-4 pt-4 pb-2 border-b border-slate-100 dark:border-slate-800"
            >
              <div
                class="inline-flex items-center gap-2 p-1 rounded-full bg-slate-50 dark:bg-slate-800"
              >
                <!-- Account tab -->
                <button
                  type="button"
                  @click="activeTab = 'account'"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium rounded-full transition shadow-sm"
                  :class="
                    activeTab === 'account'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/60'
                  "
                >
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 text-[13px] rounded-full"
                    :class="
                      activeTab === 'account'
                        ? 'bg-secondary/50 text-white'
                        : 'bg-secondary/10 text-primary'
                    "
                  >
                    <i class="mdi mdi-account-outline"></i>
                  </span>
                  <span>{{ t("account_tab_account") }}</span>
                </button>

                <!-- Security tab -->
                <button
                  type="button"
                  @click="activeTab = 'security'"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium rounded-full transition"
                  :class="
                    activeTab === 'security'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/60'
                  "
                >
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 text-[13px] rounded-full"
                    :class="
                      activeTab === 'security'
                        ? 'bg-secondary/50 text-white'
                        : 'bg-secondary/10 text-primary'
                    "
                  >
                    <i class="mdi mdi-lock-outline"></i>
                  </span>
                  <span>{{ t("account_tab_security") }}</span>
                </button>
              </div>

              <span class="hidden text-xs text-slate-400 sm:inline">
                {{ t("account_last_updated_hint") }}
              </span>
            </header>

            <!-- ACCOUNT FORM (CLIENT ONLY) -->
            <form
              v-if="activeTab === 'account'"
              class="space-y-6"
              @submit.prevent="saveSettings"
            >
              <!-- Client section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-briefcase-outline text-secondary"></i>
                  <span>{{ t("account_section_client") }}</span>
                  <span class="text-rose-500">*</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div
                    class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
                  >
                    <!-- Client name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_client_name") }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 rounded-l-md bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-l-none rounded-r-md'
                              : 'left-0'
                          "
                        >
                          <i class="mdi mdi-account-outline text-[18px]"></i>
                        </span>

                        <input
                          v-model="form.client.name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_client_name_placeholder')
                          "
                        />

                        <p
                          v-if="errors?.client?.name"
                          class="mt-1 text-xs text-red-500"
                        >
                          {{ errors.client.name[0] }}
                        </p>
                      </div>
                    </div>

                    <!-- Activity -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_activity") }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-pulse text-[18px]"></i>
                        </span>

                        <input
                          v-model="form.client.activity"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_activity_placeholder')"
                        />
                      </div>
                    </div>

                    <!-- Manager name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_manager_name") }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-account-tie-outline text-[18px]"
                          ></i>
                        </span>

                        <input
                          v-model="form.client.manager"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_manager_name_placeholder')
                          "
                        />
                      </div>
                    </div>

                    <!-- Activity Description (Client only) -->
                    <div class="lg:col-span-3 md:col-span-2">
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{
                          t("account_field_activity_description") ||
                          "Activity Description"
                        }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute top-2.5 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                          style="height: 32px"
                        >
                          <i class="mdi mdi-text-long text-[18px]"></i>
                        </span>

                        <textarea
                          v-model="form.client.activity_description"
                          rows="4"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_activity_description_placeholder') ||
                            ''
                          "
                        ></textarea>
                      </div>

                      <p
                        v-if="errors?.client?.activity_description"
                        class="mt-1 text-xs text-red-500"
                      >
                        {{ errors.client.activity_description[0] }}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Address section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-map-marker-outline text-secondary"></i>
                  <span>{{ t("account_section_address") }}</span>
                  <span class="text-rose-500">*</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- City -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_city") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-city-variant-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.address.city"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_city_placeholder')"
                        />
                      </div>
                    </div>

                    <!-- District -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_district") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-map-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.district"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_district_placeholder')"
                        />
                      </div>
                    </div>

                    <!-- Street -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_street") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-sign-direction text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.street"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_street_placeholder')"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- Building number -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_building_number") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-office-building-marker-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.address.building"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_building_number_placeholder')
                          "
                        />
                      </div>
                    </div>

                    <!-- Additional number -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_additional_number") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-pound text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.additional"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_additional_number_placeholder')
                          "
                        />
                      </div>
                    </div>

                    <!-- Postal code -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_postal_code") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-mailbox-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.postalCode"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_postal_code_placeholder')
                          "
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Short address -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("account_field_short_address") }}
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <i class="mdi mdi-home-map-marker text-[18px]"></i>
                      </span>
                      <input
                        v-model="form.address.shortAddress"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                        :placeholder="
                          t('account_field_short_address_placeholder')
                        "
                      />
                    </div>
                  </div>
                </div>
              </section>

              <!-- Bank section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-bank-outline text-secondary"></i>
                  <span>{{ t("account_section_bank") }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_holder_name") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-account-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.bank.holder"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_holder_name_placeholder')
                          "
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_iban") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-credit-card-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.bank.iban"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_iban_placeholder')"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_bank_name") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-bank text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.bank.name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_bank_name_placeholder')
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Actions -->
              <footer
                class="flex flex-col items-stretch gap-3 px-5 py-4 border-t border-slate-100 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60"
              >
                <div class="flex gap-2">
                  <button
                    type="submit"
                    :disabled="saving"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i v-if="saving" class="mdi mdi-loading mdi-spin me-2"></i>
                    {{
                      saving ? t("account_btn_saving") : t("account_btn_save")
                    }}
                  </button>

                  <button
                    type="button"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium bg-white border rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700"
                  >
                    {{ t("account_btn_cancel") }}
                  </button>
                </div>

                <p class="text-[11px] text-slate-400">
                  {{ t("account_required_fields_note") }}
                </p>
              </footer>
            </form>

            <!-- SECURITY FORM -->
            <form v-else class="space-y-6" @submit.prevent="savePassword">
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-lock-outline text-secondary"></i>
                  <span>{{ t("security_section_title") }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("security_current_password") }}
                      </label>
                      <input
                        v-model="passwordForm.current_password"
                        type="password"
                        class="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      />
                      <p
                        v-if="passwordErrors?.old_password?.[0]"
                        class="mt-1 text-xs text-red-500"
                      >
                        {{ passwordErrors.old_password[0] }}
                      </p>
                    </div>

                    <div class="hidden md:block"></div>

                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("security_new_password") }}
                      </label>
                      <input
                        v-model="passwordForm.password"
                        type="password"
                        class="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      />
                      <p
                        v-if="passwordErrors?.password?.[0]"
                        class="mt-1 text-xs text-red-500"
                      >
                        {{ passwordErrors.password[0] }}
                      </p>
                    </div>

                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("security_confirm_password") }}
                      </label>
                      <input
                        v-model="passwordForm.password_confirmation"
                        type="password"
                        class="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      />
                      <p
                        v-if="passwordErrors?.password_confirmation?.[0]"
                        class="mt-1 text-xs text-red-500"
                      >
                        {{ passwordErrors.password_confirmation[0] }}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <footer
                class="flex flex-col items-stretch gap-3 px-5 py-4 border-t border-slate-100 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60"
              >
                <div class="flex gap-2">
                  <button
                    type="submit"
                    :disabled="passwordSaving"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i
                      v-if="passwordSaving"
                      class="mdi mdi-loading mdi-spin me-2"
                    ></i>
                    {{
                      passwordSaving
                        ? t("security_btn_saving")
                        : t("security_btn_save")
                    }}
                  </button>
                </div>

                <p class="text-[11px] text-slate-400">
                  {{ t("security_password_hint") }}
                </p>
              </footer>
            </form>
          </section>
        </div>

        <Toast
          :show="toast.show"
          :type="toast.type"
          :message="toast.message"
          @update:show="toast.show = $event"
        />
      </main>
    </div>
  </div>
</template>

<script>
import { messages } from "@/i18n";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import api from "@/services/api";
import { translateCookieToastMessage } from "@/utils/cookieErrors";

export default {
  name: "AccountSettingsClient",
  components: { AdminSidebar, AdminTopbar, Toast },

  data() {
    const initial = (
      document.documentElement.lang ||
      localStorage.getItem("app_lang") ||
      "ar"
    ).toLowerCase();

    return {
      messages,
      lang: initial.startsWith("ar") ? "ar" : "en",

      isSidebarCollapsed: false,
      isSidebarOpenMobile: false,
      sidebarSearch: "",

      saving: false,
      loading: true,
      activeTab: "account",

      passwordSaving: false,

      passwordForm: {
        current_password: "",
        password: "",
        password_confirmation: "",
      },

      passwordErrors: {},

      passwordVisible: {
        current: false,
        new: false,
        confirm: false,
      },

      errors: { client: {}, address: {}, bank: {} },

      toast: { show: false, type: "success", message: "" },

      form: {
        client: {
          name: "",
          activity: "",
          manager: "",
          activity_description: "",
        },
        address: {
          city: "",
          district: "",
          street: "",
          building: "",
          additional: "",
          postalCode: "",
          shortAddress: "",
        },
        bank: { holder: "", iban: "", name: "" },
      },
    };
  },

  async mounted() {
    // ✅ init from BOTH html lang + localStorage (same logic as your working view)
    this.lang = this.readLangFromSources();

    // ✅ listen to global switcher
    this._langHandler = (e) => {
      const next = this.normalizeLang(e.detail || "ar");
      if (next !== this.lang) this.lang = next;
    };
    window.addEventListener("app:lang", this._langHandler, { passive: true });

    // ✅ observe html lang/dir but also fallback to localStorage
    this._langObserver = new MutationObserver(() => {
      const next = this.readLangFromSources();
      if (next !== this.lang) this.lang = next;
    });

    this._langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "dir"],
    });

    await this.initUserFromApi();
  },

  beforeUnmount() {
    if (this._langObserver) this._langObserver.disconnect();
    if (this._langHandler)
      window.removeEventListener("app:lang", this._langHandler);
  },

  watch: {
    lang: {
      immediate: true,
      handler(next) {
        const safe = next === "en" ? "en" : "ar";

        document.documentElement.lang = safe;
        document.documentElement.dir = safe === "ar" ? "rtl" : "ltr";

        // ✅ keep storage in sync (important for pages that read storage)
        localStorage.setItem("app_lang", safe);
      },
    },
  },

  methods: {
    // ✅ same normalize logic used everywhere
    normalizeLang(raw) {
      return String(raw || "").toLowerCase().startsWith("en") ? "en" : "ar";
    },

    // ✅ read from html OR localStorage (same as working view)
    readLangFromSources() {
      return this.normalizeLang(
        document.documentElement.lang || localStorage.getItem("app_lang") || "ar"
      );
    },

    t(key) {
      return (this.messages?.[this.lang] && this.messages[this.lang][key]) || key;
    },

    toggleSidebar() {
      if (window.innerWidth < 768)
        this.isSidebarOpenMobile = !this.isSidebarOpenMobile;
      else this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },

    showToast(message, type = "success") {
      this.toast.message = translateCookieToastMessage(message, this.t.bind(this));
      this.toast.type = type;
      this.toast.show = true;
      setTimeout(() => (this.toast.show = false), 2500);
    },

    // ✅ helper: normalize api response
    normalize(res) {
      return res?.data?.data ?? res?.data ?? null;
    },

    getCookie(name) {
      const match = document.cookie.match(
        new RegExp(
          "(?:^|; )" +
            name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") +
            "=([^;]*)"
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    },

    getClientIdFromAuthCookie() {
      const raw = this.getCookie("auth_user");
      if (!raw) return null;

      try {
        const u = JSON.parse(raw);
        return u?.client?.id || u?.client_id || u?.clientId || null;
      } catch (e) {
        console.error("Failed to parse auth_user cookie", e);
        return null;
      }
    },

    // ✅ GET client data from /clients
    async initUserFromApi() {
      this.loading = true;

      try {
        const clientId = this.getClientIdFromAuthCookie();

        if (!clientId) {
          this.showToast(this.t("cookie_err_client_id_not_found"), "error");
          return;
        }

        const res = await api.get(`/clients/${clientId}`, {
          headers: { Accept: "application/json" },
        });

        const payload = this.normalize(res);
        const client = payload?.client ?? payload;

        // ---- client ----
        this.form.client.name = client?.name ?? "";
        this.form.client.activity = client?.activity ?? "";
        this.form.client.manager = client?.manager_name ?? "";
        this.form.client.activity_description =
          client?.activity_description ?? "";

        // ---- address ----
        const addr = payload?.address ?? client?.address ?? null;
        this.form.address.city = addr?.city ?? "";
        this.form.address.district = addr?.district ?? "";
        this.form.address.street = addr?.street ?? "";
        this.form.address.building = addr?.building_number ?? "";
        this.form.address.additional = addr?.additional_number ?? "";
        this.form.address.postalCode = addr?.postal_code ?? "";
        this.form.address.shortAddress = addr?.short_address ?? "";

        // ---- bank ----
        const bank =
          payload?.bank ?? client?.bank ?? client?.bank_account ?? null;
        this.form.bank.holder = bank?.account_holder_name ?? "";
        this.form.bank.iban = bank?.account_number ?? "";
        this.form.bank.name = bank?.bank_name ?? "";
      } catch (e) {
        console.error("GET /clients/{id} failed:", e);
        this.showToast(this.t("account_saved_failed"), "error");
      } finally {
        this.loading = false;
      }
    },

    // ✅ POST update to /clients
    async saveSettings() {
      if (this.loading) return;

      this.saving = true;
      this.errors = { client: {}, address: {}, bank: {} };

      try {
        const payload = {
          client: {
            name: this.form.client.name,
            activity: this.form.client.activity,
            manager_name: this.form.client.manager,
            activity_description: this.form.client.activity_description,
          },
          address: {
            city: this.form.address.city,
            district: this.form.address.district,
            street: this.form.address.street,
            building_number: this.form.address.building,
            additional_number: this.form.address.additional,
            postal_code: this.form.address.postalCode,
            short_address: this.form.address.shortAddress,
          },
          bank: {
            account_holder_name: this.form.bank.holder,
            account_number: this.form.bank.iban,
            bank_name: this.form.bank.name,
          },
        };

        const res = await api.post("/clients", payload, {
          headers: { Accept: "application/json" },
        });

        const updated = this.normalize(res);
        if (updated) {
          const client = updated?.client ?? updated;

          this.form.client.name = client?.name ?? this.form.client.name;
          this.form.client.activity =
            client?.activity ?? this.form.client.activity;
          this.form.client.manager =
            client?.manager_name ?? this.form.client.manager;
          this.form.client.activity_description =
            client?.activity_description ?? this.form.client.activity_description;

          const addr = updated?.address ?? client?.address ?? null;
          if (addr) {
            this.form.address.city = addr?.city ?? this.form.address.city;
            this.form.address.district =
              addr?.district ?? this.form.address.district;
            this.form.address.street = addr?.street ?? this.form.address.street;
            this.form.address.building =
              addr?.building_number ?? this.form.address.building;
            this.form.address.additional =
              addr?.additional_number ?? this.form.address.additional;
            this.form.address.postalCode =
              addr?.postal_code ?? this.form.address.postalCode;
            this.form.address.shortAddress =
              addr?.short_address ?? this.form.address.shortAddress;
          }

          const bank =
            updated?.bank ?? client?.bank ?? client?.bank_account ?? null;
          if (bank) {
            this.form.bank.holder =
              bank?.account_holder_name ?? this.form.bank.holder;
            this.form.bank.iban = bank?.account_number ?? this.form.bank.iban;
            this.form.bank.name = bank?.bank_name ?? this.form.bank.name;
          }
        }

        this.showToast(this.t("account_saved_success"), "success");
      } catch (err) {
        const res = err.response?.data;
        console.error("POST /clients failed:", res || err);

        if (res?.errors) {
          this.errors = res.errors;

          const msg = Object.values(res.errors)
            .flatMap((v) => {
              if (Array.isArray(v)) return v;
              if (v && typeof v === "object") return Object.values(v).flat();
              return [];
            })
            .filter(Boolean)
            .join("\n");

          this.showToast(
            msg || (res?.message ?? this.t("account_saved_failed")),
            "error"
          );
        } else {
          this.showToast(
            res?.message || this.t("account_saved_failed"),
            "error"
          );
        }
      } finally {
        this.saving = false;
      }
    },

    async savePassword() {
      if (this.passwordSaving || this.loading) return;

      this.passwordSaving = true;
      this.passwordErrors = {};

      try {
        const bodyData = {
          old_password: this.passwordForm.current_password,
          password: this.passwordForm.password,
          password_confirmation: this.passwordForm.password_confirmation,
        };

        const { data: res } = await api.patch("/users/updatePassword", bodyData, {
          headers: { Accept: "application/json" },
        });

        this.showToast(
          res?.message || this.t("security_password_success"),
          "success"
        );

        this.passwordForm.current_password = "";
        this.passwordForm.password = "";
        this.passwordForm.password_confirmation = "";
      } catch (err) {
        const res = err.response?.data;

        if (res?.errors) {
          this.passwordErrors = res.errors;

          const msg = Object.values(res.errors)
            .flatMap((v) => (Array.isArray(v) ? v : Object.values(v || {}).flat()))
            .filter(Boolean)
            .join("\n");

          this.showToast(msg, "error");
        } else {
          this.showToast(
            res?.message || this.t("security_password_failed"),
            "error"
          );
        }
      } finally {
        this.passwordSaving = false;
      }
    },

    togglePasswordVisibility(field) {
      this.passwordVisible[field] = !this.passwordVisible[field];
    },

    resetPasswordForm() {
      this.passwordForm.current_password = "";
      this.passwordForm.password = "";
      this.passwordForm.password_confirmation = "";

      this.passwordErrors = {};
      this.passwordVisible = { current: false, new: false, confirm: false };
    },
  },
};
</script>